import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Usuario } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves, useUsuarios } from "../api/consultas";
import { useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { Carregando, ErroCarregamento, formatarData, Tabela } from "../components/ui";

export function Usuarios() {
  const { data, isLoading, error } = useUsuarios();
  const { usuario: atual } = useAuth();
  const { avisar, avisarErro } = useAvisos();
  const queryClient = useQueryClient();
  const recarregar = () => queryClient.invalidateQueries({ queryKey: chaves.usuarios });

  const atualizar = useMutation({
    mutationFn: (u: Pick<Usuario, "id" | "papel" | "pode_editar">) =>
      api<{ mensagem: string }>(`/api/usuarios/${u.id}`, { method: "PATCH", body: { papel: u.papel, pode_editar: u.pode_editar } }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      recarregar();
    },
    onError: avisarErro,
  });

  const [senhaGerada, setSenhaGerada] = useState<{ nome: string; senha_temporaria: string } | null>(null);

  const redefinirSenha = useMutation({
    mutationFn: (id: number) =>
      api<{ nome: string; senha_temporaria: string }>(`/api/usuarios/${id}/redefinir-senha`, { method: "POST" }),
    onSuccess: (r) => {
      setSenhaGerada(r);
      recarregar();
    },
    onError: avisarErro,
  });

  const aprovar = useMutation({
    mutationFn: (id: number) => api<{ mensagem: string }>(`/api/usuarios/${id}/aprovar`, { method: "POST" }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      recarregar();
    },
    onError: avisarErro,
  });

  const remover = useMutation({
    mutationFn: (id: number) => api<{ mensagem: string }>(`/api/usuarios/${id}`, { method: "DELETE" }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      recarregar();
    },
    onError: avisarErro,
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;

  const pendentes = data.filter((u) => u.aprovado === false).length;

  return (
    <>
      <h1>Usuários</h1>
      {pendentes > 0 && (
        <div className="card" style={{ borderColor: "var(--embasa-accent, #f59e0b)" }}>
          <strong>{pendentes} cadastro(s) aguardando sua aprovação.</strong> Aprove quem deve ter acesso ou remova os demais.
        </div>
      )}
      <p className="note">
        Só o administrador vê esta tela. Marque "Editar" para liberar criação/edição de orçamentos e municípios a uma conta.
      </p>
      {senhaGerada && (
        <div className="card" style={{ borderColor: "var(--embasa-accent)" }}>
          <p style={{ margin: 0 }}>
            Nova senha temporária para <strong>{senhaGerada.nome}</strong>:{" "}
            <code style={{ fontSize: "1.2rem", userSelect: "all" }}>{senhaGerada.senha_temporaria}</code>
          </p>
          <p className="note">
            Repasse por telefone/WhatsApp (fora do sistema). Ele deve trocar em "Trocar senha" assim que entrar. Esta senha
            não aparece de novo depois que você fechar este aviso.
          </p>
          <button type="button" className="btn-ghost" onClick={() => setSenhaGerada(null)}>
            Fechar
          </button>
        </div>
      )}
      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Papel</th>
              <th>Pode editar?</th>
              <th>Desde</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id}>
                <td>
                  {u.nome}{" "}
                  {u.aprovado === false && <span className="badge badge-orange">aguardando aprovação</span>}{" "}
                  {u.pediu_nova_senha && <span className="badge badge-orange">pediu nova senha</span>}
                </td>
                <td>{u.email}</td>
                <td>{u.papel === "admin" ? <span className="badge badge-orange">admin</span> : <span className="badge badge-muted">usuário</span>}</td>
                <td>{u.pode_editar ? <span className="badge badge-green">sim</span> : <span className="badge badge-muted">não</span>}</td>
                <td>{formatarData(u.criado_em)}</td>
                <td className="no-print">
                  {u.id !== atual?.id ? (
                    <div className="form-row" style={{ margin: 0, alignItems: "center", gap: 6 }}>
                      {u.aprovado === false && (
                        <button className="btn" type="button" disabled={aprovar.isPending} onClick={() => aprovar.mutate(u.id)}>
                          Aprovar
                        </button>
                      )}
                      <label style={{ flexDirection: "row", alignItems: "center", gap: 4, fontSize: "0.8rem" }}>
                        <input
                          type="checkbox"
                          checked={u.pode_editar}
                          disabled={atualizar.isPending}
                          onChange={(e) => atualizar.mutate({ id: u.id, papel: u.papel, pode_editar: e.target.checked })}
                        />
                        Editar
                      </label>
                      <button
                        className="btn-ghost"
                        type="button"
                        disabled={redefinirSenha.isPending}
                        onClick={() => {
                          if (confirm(`Gerar uma nova senha temporária para ${u.nome}? A senha atual deixa de funcionar.`)) {
                            redefinirSenha.mutate(u.id);
                          }
                        }}
                      >
                        Gerar nova senha
                      </button>
                      <button
                        className="btn-danger"
                        type="button"
                        disabled={remover.isPending}
                        onClick={() => {
                          if (confirm(`Remover a conta de ${u.nome}?`)) remover.mutate(u.id);
                        }}
                      >
                        remover
                      </button>
                    </div>
                  ) : (
                    <span className="note">(você)</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>
    </>
  );
}
